import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Button, Divider, Stack, Card, Link as JoyLink,
  Input, IconButton, CircularProgress, Select, Option, FormControl, FormLabel,
} from '@mui/joy';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import WebhookIcon from '@mui/icons-material/Webhook';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { getProvider } from './providers';
import { getProviderQueries } from './providerQueryMap';
import { ATSCandidateImportDialog } from './index';
import useUser from '../auth/useUser';
import LargeLoader from '../LargeLoader';
import { useFeedback } from '../feedback/Service';
import ME from '../../gql/common/me.query';
import useTracking from '../useTracking';

const NonATSInviteCandidates = ({ user }) => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const feedback = useFeedback();
  const { trackCopyJobUrl, trackOpenDemo } = useTracking();
  const clientId = user?.client?._id;
  const job = useMemo(
    () => user?.client?.jobs?.find(j => j._id === jobId),
    [user, jobId]
  );
  const interviewLink = `${window.location.origin}/company/${clientId}/job/${jobId}/intro`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(interviewLink);
    trackCopyJobUrl(clientId, jobId, job?.title);
    feedback.snackbar({ text: 'Interview link copied to clipboard!', type: 'success' });
  };

  const handleDemoInterview = () => {
    trackOpenDemo(clientId, jobId);
    window.open(interviewLink, '_blank');
  };

  return (
    <Box
      sx={{
        py: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        width: '100%',
        maxWidth: 'md',
        mx: 'auto',
      }}
    >
      <Box>
        <Typography level="h4" gutterBottom>Invite your candidates</Typography>
        {job?.title && (
          <Typography level="body-sm" color="neutral">for {job.title}</Typography>
        )}
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <ContentCopyIcon sx={{ fontSize: 36, color: 'primary.400', mb: 1 }} />
          <Typography level="title-md">Copy interview link</Typography>
          <Typography level="body-sm" color="neutral" sx={{ mt: 0.5, mb: 2 }}>
            Share it with candidates or post it on job boards.
          </Typography>
          <Button
            variant="solid"
            color="primary"
            startDecorator={<ContentCopyIcon />}
            onClick={handleCopyLink}
            sx={{ mt: 'auto', alignSelf: 'flex-start' }}
          >
            Copy interview link
          </Button>
        </Card>

        <Card variant="outlined" sx={{ flex: 1 }}>
          <PlayArrowIcon sx={{ fontSize: 36, color: 'primary.400', mb: 1 }} />
          <Typography level="title-md">Start demo interview</Typography>
          <Typography level="body-sm" color="neutral" sx={{ mt: 0.5, mb: 2 }}>
            Experience the candidate flow before sharing the link.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<PlayArrowIcon />}
            onClick={handleDemoInterview}
            sx={{ mt: 'auto', alignSelf: 'flex-start' }}
          >
            Start demo interview
          </Button>
        </Card>
      </Stack>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="solid" color="primary" onClick={() => navigate('/jobs')}>
          Go to Jobs
        </Button>
      </Box>
    </Box>
  );
};

const ATSInviteCandidatesContent = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [currentKey, setCurrentKey] = useState(null);
  const [isEditingSourceStage, setIsEditingSourceStage] = useState(false);
  const [editSourceStageId, setEditSourceStageId] = useState(null);
  const [isEditingTargetStage, setIsEditingTargetStage] = useState(false);
  const [editTargetStageId, setEditTargetStageId] = useState(null);
  const feedback = useFeedback();
  const { trackOpenDemo } = useTracking();
  const { user, loading: userLoading } = useUser();
  const providerId = user?.client?.selectedATSProvider;
  const provider = providerId ? getProvider(providerId) : null;
  const pq = providerId ? getProviderQueries(providerId) : null;

  const { data: statusData, loading: statusLoading } = useQuery(pq?.GET_STATUS, {
    skip: !pq,
    fetchPolicy: 'network-only',
  });

  const { data: keyData, loading: keyLoading } = useQuery(pq?.GET_ASSESSMENT_API_KEY, {
    skip: !pq,
    fetchPolicy: 'network-only',
    onCompleted: (d) => setCurrentKey(d?.[pq.assessmentKeyField] ?? null),
  });

  const { data: jobsData, loading: jobsLoading } = useQuery(pq?.LIST_JOBS, {
    skip: !pq,
    fetchPolicy: 'network-only',
  });

  // Sync currentKey from query when data arrives
  useEffect(() => {
    if (keyData) setCurrentKey(keyData?.[pq?.assessmentKeyField] ?? null);
  }, [keyData, pq]);

  const [generateKey, { loading: generating }] = useMutation(pq?.GENERATE_ASSESSMENT_API_KEY, {
    onCompleted: (d) => {
      const key = d?.[pq.generateAssessmentKeyField];
      setCurrentKey(key);
      setShowRegenerateConfirm(false);
      feedback.snackbar({ text: 'API key generated — copy it now', type: 'success' });
    },
    onError: () => feedback.snackbar({ text: 'Failed to generate key', type: 'error' }),
  });

  const handleCopyKey = () => {
    if (!currentKey) return;
    navigator.clipboard.writeText(currentKey);
    feedback.snackbar({ text: 'Copied to clipboard', type: 'success' });
  };

  // Guard: no provider → jobs
  useEffect(() => {
    if (!userLoading && !providerId) {
      navigate('/jobs');
    }
  }, [userLoading, providerId, navigate]);

  // Guard: not connected → connect-ats
  useEffect(() => {
    if (!statusLoading && statusData && pq) {
      const status = statusData[pq.statusField];
      if (!status?.enabled) {
        navigate('/onboarding/connect-ats');
      }
    }
  }, [statusData, statusLoading, pq, navigate]);

  const linkedATSJob = useMemo(() => {
    if (!jobsData || !pq || !jobId) return null;
    const jobs = jobsData[pq.jobsField] ?? [];
    return jobs.find(j => j.linkedCavalryJobId === jobId) ?? null;
  }, [jobsData, pq, jobId]);

  // Guard: no linked ATS job → import-job
  useEffect(() => {
    if (!jobsLoading && jobsData && !linkedATSJob) {
      navigate('/onboarding/import-job');
    }
  }, [jobsLoading, jobsData, linkedATSJob, navigate]);

  const job = useMemo(
    () => user?.client?.jobs?.find(j => j._id === jobId),
    [user, jobId]
  );

  const clientId = user?.client?._id;

  const effectiveMode = job?.[pq?.flowModeField] || pq?.defaultFlowMode;
  const isAssessmentOnly = effectiveMode === 'assessment_only';
  const atsSourceStageId = job?.atsSourceStageId;
  const atsTargetStageId = job?.atsTargetStageId;

  const { data: stagesData, loading: loadingStages } = useQuery(pq?.LIST_INTERVIEW_STAGES, {
    variables: pq && linkedATSJob ? { [pq.jobIdVar]: linkedATSJob.id } : {},
    skip: !pq || !linkedATSJob || isAssessmentOnly,
    fetchPolicy: 'network-only',
  });

  const stages = useMemo(() => {
    const raw = stagesData?.[pq?.stagesField] ?? [];
    return [...raw].sort((a, b) => (a[pq?.stageSortField] ?? 0) - (b[pq?.stageSortField] ?? 0));
  }, [stagesData, pq]);

  const getStageName = (id) => stages.find(s => s.id === id)?.name ?? id;

  const [setMode, { loading: settingMode }] = useMutation(pq?.LINK_JOB, {
    refetchQueries: [{ query: ME }],
    awaitRefetchQueries: true,
    onCompleted: () => feedback.snackbar({ text: 'Mode saved', type: 'success' }),
    onError: () => feedback.snackbar({ text: 'Failed to save mode', type: 'error' }),
  });

  const [updateSourceStage, { loading: updatingSourceStage }] = useMutation(pq?.LINK_JOB, {
    refetchQueries: [{ query: ME }],
    awaitRefetchQueries: true,
    onCompleted: () => {
      setIsEditingSourceStage(false);
      feedback.snackbar({ text: 'Source stage saved', type: 'success' });
    },
    onError: () => feedback.snackbar({ text: 'Failed to save stage', type: 'error' }),
  });

  const [updateTargetStage, { loading: updatingTargetStage }] = useMutation(pq?.LINK_JOB, {
    refetchQueries: [{ query: ME }],
    awaitRefetchQueries: true,
    onCompleted: () => {
      setIsEditingTargetStage(false);
      feedback.snackbar({ text: 'Target stage saved', type: 'success' });
    },
    onError: () => feedback.snackbar({ text: 'Failed to save stage', type: 'error' }),
  });

  const handleModeSelect = (mode) => {
    if (mode === effectiveMode || settingMode) return;
    setMode({
      variables: {
        [pq.jobIdVar]: linkedATSJob.id,
        cavalryJobId: jobId,
        mode,
        sourceStageId: atsSourceStageId,
        targetStageId: atsTargetStageId,
      },
    });
  };

  if (userLoading || statusLoading || jobsLoading) return <LargeLoader />;
  if (!provider || !pq || !linkedATSJob) return null;

  const isKeyReady = !!currentKey;

  const handleDemoInterview = () => {
    trackOpenDemo(clientId, jobId);
    window.open(`/company/${clientId}/job/${jobId}/intro`, '_blank');
  };

  const modeOptions = [
    {
      value: pq.defaultFlowMode,
      icon: <WebhookIcon sx={{ fontSize: 32, color: 'primary.400', mb: 1 }} />,
      title: pq.importModeName,
      description: 'Candidates are imported manually or automatically synced when they reach a pipeline stage you configure.',
      note: 'Best for teams who want full control over which candidates enter Cavalry.',
    },
    {
      value: 'assessment_only',
      icon: <AssessmentIcon sx={{ fontSize: 32, color: 'primary.400', mb: 1 }} />,
      title: 'Assessment Only',
      description: `${provider.name} triggers the Cavalry assessment automatically when a candidate reaches the interview stage.`,
      note: 'Best for high-volume pipelines. Requires the API key setup below.',
    },
  ];

  return (
    <Box
      sx={{
        py: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        width: '100%',
        maxWidth: 'md',
        mx: 'auto',
      }}
    >
      <Box>
        <Typography level="h4" gutterBottom>Set up candidate pipeline</Typography>
        {job?.title && (
          <Typography level="body-sm" color="neutral">for {job.title}</Typography>
        )}
      </Box>

      {/* Mode selection */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {modeOptions.map((opt) => {
          const isSelected = effectiveMode === opt.value;
          return (
            <Card
              key={opt.value}
              variant="outlined"
              onClick={() => handleModeSelect(opt.value)}
              sx={{
                flex: 1,
                cursor: settingMode ? 'not-allowed' : 'pointer',
                transition: 'border-color 0.15s, box-shadow 0.15s',
                borderColor: isSelected ? 'primary.400' : undefined,
                boxShadow: isSelected ? 'sm' : undefined,
                '&:hover': !settingMode ? { borderColor: 'primary.400', boxShadow: 'sm' } : {},
              }}
            >
              {opt.icon}
              <Typography level="title-sm">{opt.title}</Typography>
              <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>{opt.description}</Typography>
              <Typography level="body-xs" color="primary" sx={{ mt: 1 }}>{opt.note}</Typography>
            </Card>
          );
        })}
      </Stack>

      {/* Section A — Assessment API key (assessment_only mode) */}
      {isAssessmentOnly && (
        <Card variant="outlined">
          <Typography level="title-md" gutterBottom>Assessment API key</Typography>
          <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
            Generate an API key and add it to {provider.name}. {provider.name} will then automatically send candidates to Cavalry when the assessment is triggered.
          </Typography>
          {keyLoading ? (
            <CircularProgress size="sm" />
          ) : isKeyReady ? (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Input
                  readOnly
                  value={currentKey}
                  size="sm"
                  sx={{ fontFamily: 'monospace', flex: 1 }}
                />
                <IconButton size="sm" variant="outlined" onClick={handleCopyKey} title="Copy to clipboard">
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Stack>
              {provider.assessmentSettingsUrl && (
                <JoyLink
                  href={provider.assessmentSettingsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  level="body-sm"
                >
                  Open {provider.name} integration settings &rarr;
                </JoyLink>
              )}
              {provider.assessmentInstructionsUrl && (
                <JoyLink
                  href={provider.assessmentInstructionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  level="body-sm"
                >
                  View setup instructions &rarr;
                </JoyLink>
              )}
              {showRegenerateConfirm ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography level="body-xs" color="danger">
                    This invalidates the current key &mdash; you must update {provider.name} with the new key.
                  </Typography>
                  <Button size="sm" color="danger" variant="soft" loading={generating} onClick={() => generateKey()}>
                    Confirm
                  </Button>
                  <Button size="sm" variant="plain" color="neutral" onClick={() => setShowRegenerateConfirm(false)}>
                    Cancel
                  </Button>
                </Stack>
              ) : (
                <Button
                  size="sm"
                  variant="plain"
                  color="neutral"
                  sx={{ alignSelf: 'flex-start' }}
                  onClick={() => setShowRegenerateConfirm(true)}
                >
                  Regenerate key
                </Button>
              )}
            </Stack>
          ) : (
            <Button variant="outlined" color="primary" loading={generating} onClick={() => generateKey()}>
              Generate API key
            </Button>
          )}
        </Card>
      )}

      {/* Section B — Stage setup (non-assessment mode) */}
      {!isAssessmentOnly && (
        <Card variant="outlined">
          <Box sx={{ mb: 2 }}>
            <Typography level="title-md" gutterBottom>Pipeline stage setup</Typography>
            <Typography level="body-sm" color="neutral" gutterBottom>
              Configure which {provider.name} stages trigger automatic candidate import and where candidates land after completing their interview.
            </Typography>
            {provider.webhookInstructionsUrl && (
              <JoyLink
                href={provider.webhookInstructionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                level="body-sm"
              >
                View setup instructions &rarr;
              </JoyLink>
            )}
          </Box>
          <Stack spacing={2}>
            {/* Source stage */}
            <Box sx={{ p: 2, bgcolor: 'neutral.softBg', borderRadius: 'sm' }}>
              {isEditingSourceStage ? (
                <Stack spacing={1}>
                  <FormControl size="sm">
                    <FormLabel>Source stage — auto-import when candidates reach</FormLabel>
                    {loadingStages ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size="sm" />
                        <Typography level="body-sm">Loading stages...</Typography>
                      </Box>
                    ) : (
                      <Select
                        size="sm"
                        placeholder="Select stage (optional)"
                        value={editSourceStageId}
                        onChange={(_, value) => setEditSourceStageId(value)}
                      >
                        {stages.map((stage) => (
                          <Option key={stage.id} value={stage.id}>{stage.name}</Option>
                        ))}
                      </Select>
                    )}
                    <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                      Candidates who reach this stage in {provider.name} will be automatically imported. Leave blank for manual import only.
                    </Typography>
                  </FormControl>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="sm"
                      variant="solid"
                      loading={updatingSourceStage}
                      onClick={() => updateSourceStage({
                        variables: {
                          [pq.jobIdVar]: linkedATSJob.id,
                          cavalryJobId: jobId,
                          sourceStageId: editSourceStageId,
                          targetStageId: atsTargetStageId,
                        },
                      })}
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="plain"
                      color="neutral"
                      onClick={() => { setIsEditingSourceStage(false); setEditSourceStageId(null); }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography level="body-xs" color="neutral">Source stage</Typography>
                    <Typography level="body-sm">
                      {atsSourceStageId
                        ? (stages.length > 0 ? getStageName(atsSourceStageId) : 'Loading...')
                        : 'Not set'}
                    </Typography>
                  </Box>
                  <Button
                    size="sm"
                    variant="plain"
                    startDecorator={<EditIcon />}
                    onClick={() => { setIsEditingSourceStage(true); setEditSourceStageId(atsSourceStageId || null); }}
                  >
                    Edit
                  </Button>
                </Box>
              )}
            </Box>

            {/* Target stage */}
            <Box sx={{ p: 2, bgcolor: 'neutral.softBg', borderRadius: 'sm' }}>
              {isEditingTargetStage ? (
                <Stack spacing={1}>
                  <FormControl size="sm">
                    <FormLabel>Target stage — move candidates here after interview</FormLabel>
                    {loadingStages ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size="sm" />
                        <Typography level="body-sm">Loading stages...</Typography>
                      </Box>
                    ) : (
                      <Select
                        size="sm"
                        placeholder="Select stage (optional)"
                        value={editTargetStageId}
                        onChange={(_, value) => setEditTargetStageId(value)}
                      >
                        {stages.map((stage) => (
                          <Option key={stage.id} value={stage.id}>{stage.name}</Option>
                        ))}
                      </Select>
                    )}
                    <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                      After completing the Cavalry interview, candidates will be moved to this stage. Optional.
                    </Typography>
                  </FormControl>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="sm"
                      variant="solid"
                      loading={updatingTargetStage}
                      onClick={() => updateTargetStage({
                        variables: {
                          [pq.jobIdVar]: linkedATSJob.id,
                          cavalryJobId: jobId,
                          sourceStageId: atsSourceStageId,
                          targetStageId: editTargetStageId,
                        },
                      })}
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="plain"
                      color="neutral"
                      onClick={() => { setIsEditingTargetStage(false); setEditTargetStageId(null); }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography level="body-xs" color="neutral">Target stage</Typography>
                    <Typography level="body-sm">
                      {atsTargetStageId
                        ? (stages.length > 0 ? getStageName(atsTargetStageId) : 'Loading...')
                        : 'Not set'}
                    </Typography>
                  </Box>
                  <Button
                    size="sm"
                    variant="plain"
                    startDecorator={<EditIcon />}
                    onClick={() => { setIsEditingTargetStage(true); setEditTargetStageId(atsTargetStageId || null); }}
                  >
                    Edit
                  </Button>
                </Box>
              )}
            </Box>
          </Stack>
        </Card>
      )}

      {/* Section C — Import candidates (non-assessment mode) */}
      {!isAssessmentOnly && (
        <Card variant="outlined">
          <Typography level="title-md" gutterBottom>Import candidates from {provider.name}</Typography>
          <Typography level="body-sm" color="neutral">
            Select candidates from your {provider.name} job and import them into Cavalry.
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setImportDialogOpen(true)}
            >
              Import candidates
            </Button>
          </Box>
        </Card>
      )}

      <Divider />

      {/* Section D — Demo (always shown) */}
      <Card variant="soft" color="neutral">
        <Stack direction="row" spacing={2} alignItems="center">
          <PlayArrowIcon sx={{ fontSize: 36, color: 'primary.400' }} />
          <Box sx={{ flex: 1 }}>
            <Typography level="title-sm">Try the interview yourself</Typography>
            <Typography level="body-xs" color="neutral">
              Experience the candidate flow before inviting your team.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            startDecorator={<PlayArrowIcon />}
            onClick={handleDemoInterview}
          >
            Start demo interview
          </Button>
        </Stack>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="solid" color="primary" onClick={() => navigate('/jobs')}>
          Go to Jobs
        </Button>
      </Box>

      {importDialogOpen && (
        <ATSCandidateImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          atsJobId={linkedATSJob.id}
          cavalryJobId={jobId}
          providerId={providerId}
        />
      )}
    </Box>
  );
};

const ATSInviteCandidates = () => {
  const { user, loading: userLoading } = useUser();

  if (userLoading) return <LargeLoader />;
  if (user?.client?.usesATS === false) {
    return <NonATSInviteCandidates user={user} />;
  }

  return <ATSInviteCandidatesContent />;
};

export default ATSInviteCandidates;
